// per docs/shape-uc-21-registrator-entity-file.md §3 Endpoints
import { apiClient } from '../../app/api-client';

export interface SystemCredentialState {
  configured: boolean;
  active: boolean;
}

export interface SystemCredentialDetail extends SystemCredentialState {
  clientId: string;
  allowedIps: string[];
  allowedIpCidrs: string[];
  // The admitted addresses currently switched off. Suspending one is not
  // removing it from the admitted list, so the lists travel apart: the System
  // Administrator's (caiet UC21-08) and the Registrar Administrator's (UC31-06),
  // independent under CF-48.
  suspendedIps: string[];
  registrarSuspendedIps: string[];
  // CF-48: the System Administrator's switch on the file is its own block; the
  // Registrar Administrator's is shown beside it, never lifted from here.
  blockedBySystemAdmin: boolean;
  blockedByRegistrar: boolean;
  createdAtIso: string | null;
  lastUsedAtIso: string | null;
}

// The Registrar's legal status as RSUD last reported it, kept apart from `active`; null means
// RSUD has never answered for it. `checkedAtIso` is when RSUD was last heard from.
export interface RsudState {
  statut: string | null;
  checkedAtIso: string | null;
}

export interface SiteCounts {
  total: number;
  active: number;
  inactive: number;
}

export interface Registrar {
  id: string;
  idno: string;
  name: string;
  forma: string;
  adresa: string;
  // caiet UC21-05, NC-UC10-01 — the organisation code MPass uses: the IDNO
  // followed by 0000.
  mpassOrgCode: string;
  active: boolean;
  source: 'RSUD' | 'manual';
  kind: string;
  createdAtIso: string;
  updatedAtIso: string;
  // The optimistic lock the correction sends back (spec 052, R2): a save made
  // against a version someone else has since moved is refused, not merged.
  rowVersion: number;
}

export interface RegistrarRow extends Registrar {
  systemCredential: SystemCredentialState;
  rsud: RsudState;
  sites: SiteCounts;
}

export interface RegistrarDetail extends Registrar {
  systemCredential: SystemCredentialDetail;
  sites: SiteCounts;
  rsud: RsudState;
}

export interface Site {
  id: string;
  registrarId: string;
  internalCode: string;
  name: string;
  tip: string;
  adresa: string;
  geographyId: string;
  mpassOrgCode: string;
  // CF-48: `active` is "neither block set" (docs/shape-ops-block-levels.md).
  active: boolean;
  blockedBySystemAdmin: boolean;
  blockedByRegistrar: boolean;
  updatedAtIso: string;
  // Who made the change `updatedAtIso` dates — the newest row of the site's
  // history. The catalogue stores no editor name (BUG-1-009), so the author is the
  // masked id and the role; both are empty for a site with no history.
  lastChangeActorId: string;
  lastChangeActorRole: string;
}

export interface SubmissionRow {
  id: string;
  confirmedAtIso: string;
  source: string;
  rowCount: number;
  operatorIdnp: string;
  // The wireframe's "Locație" and "Raportat de" (RegistratorDetalii.tsx:527-533).
  siteName: string;
  operatorName: string;
}

// The RSUD lifecycle values SIMSM has a translation for. Anything else is
// rendered raw: MConnect sends whatever the state register holds, and printing
// an untranslated key on screen is the defect a fallback exists to prevent
// (AGENTS.md R3). `INACTIV` is here because the MConnect fake emits it and a
// real feed may too, even though the wireframe draws only the other three.
export const RSUD_STATUT_LABELS = ['ACTIV', 'SUSPENDAT', 'RADIAT', 'INACTIV'] as const;

export function rsudStatutLabel(t: (key: string) => string, statut: string): string {
  return (RSUD_STATUT_LABELS as readonly string[]).includes(statut)
    ? t(`registrars.rsud.statut.${statut}`)
    : statut;
}

// caiet UC21-01 — the four facets the wireframe's filter panel draws
// (AdminRegistratori.tsx:319-380). Every one is a server-side narrowing, so the
// count the footer shows is the count the query returned.
export interface RegistrarFilter {
  q?: string;
  kind?: string;
  /** Entity status. `undefined` is "both", which is not the same as `false`. */
  active?: boolean | undefined;
  /** The RSUD statut of the newest MConnect event: ACTIV | SUSPENDAT | RADIAT. */
  rsud?: string;
  /** Whether a Sistem Registrator credential exists at all. */
  system?: 'configured' | 'unconfigured' | '';
}

export async function listRegistrars(filter: RegistrarFilter): Promise<RegistrarRow[]> {
  const params: Record<string, string> = {};
  if (filter.q) params['q'] = filter.q;
  if (filter.kind) params['kind'] = filter.kind;
  if (filter.active !== undefined) params['active'] = String(filter.active);
  if (filter.rsud) params['rsud'] = filter.rsud;
  if (filter.system) params['system'] = filter.system;
  const { data } = await apiClient.get<{ registrars: RegistrarRow[] }>('/admin/registrars', {
    params,
  });
  return data.registrars ?? [];
}

export async function getRegistrar(idno: string): Promise<RegistrarDetail> {
  const { data } = await apiClient.get<RegistrarDetail>(`/admin/registrars/${idno}`);
  return data;
}

export async function listRegistrarSites(idno: string): Promise<Site[]> {
  const { data } = await apiClient.get<{ sites: Site[] }>('/admin/sites', {
    params: { registrarId: idno },
  });
  return data.sites ?? [];
}

// The tab pages the rows it holds ten at a time, as the wireframe does; the
// register behind "Toate raportările" is the way to the rest.
export async function listRegistrarSubmissions(
  idno: string,
  pageSize = 50,
): Promise<{
  submissions: SubmissionRow[];
  totalCount: number;
}> {
  const { data } = await apiClient.get<{ submissions: SubmissionRow[]; totalCount: number }>(
    '/stock/submissions',
    { params: { registrar: idno, pageSize } },
  );
  return { submissions: data.submissions ?? [], totalCount: data.totalCount ?? 0 };
}

// The summary's "Ultimul raport" (RegistratorDetalii.tsx:312-321). The register
// is already newest first, so one row answers both "when" and "ever".
export async function latestSubmission(
  idno: string,
): Promise<{ totalCount: number; confirmedAtIso: string | null }> {
  const { submissions, totalCount } = await listRegistrarSubmissions(idno, 1);
  return { totalCount, confirmedAtIso: submissions[0]?.confirmedAtIso ?? null };
}

// caiet UC21-07 — activate or inactivate the Registrar. The bff takes the
// Registrar System's credentials down with an inactivation, and leaves them
// where they were on a reactivation.
export async function setRegistrarActive(id: string, active: boolean): Promise<void> {
  await apiClient.patch(`/admin/registrars/${id}`, { active });
}

// caiet UC21-04 — pull the denumire and the statut from RSUD through MConnect
// and apply them. The refusal codes the screen distinguishes are
// RSUD_ENTITY_NOT_FOUND (RSUD does not know this IDNO) and RSUD_UNAVAILABLE
// (RSUD could not be reached); both arrive as an ApiRefusal like any other.
export interface RsudRefreshResult {
  applied: boolean;
  reason: string;
  source: string;
  name: string;
  statut: string;
}

export async function refreshFromRsud(idno: string): Promise<RsudRefreshResult> {
  const { data } = await apiClient.post<RsudRefreshResult>(
    `/admin/registrars/${idno}/rsud-refresh`,
  );
  return data;
}

// caiet UC21-10 — the Registrar's own edits. Its own read rather than the
// generic `/admin/history`: the trail lives in `catalog.registrar_history`,
// which the four-table catalog union does not cover.
export interface RegistrarHistoryEntry {
  id: string;
  entityId: string;
  entityLabel: string;
  changeType: string;
  actorId: string;
  actorRole: string;
  // No name (CF-63, SEC-14): the trail keeps the masked IDNP and the role, and the screen
  // shows the role or the source as the author, never a person's name.
  source: string;
  recordedAtIso: string;
  changes: { field: string; before: string; after: string; redacted: boolean }[];
  // The MConnect event an automatic edit applied (UC71-02, TC-UC71-02-01); empty
  // or absent for every other row.
  eventId?: string;
}

export async function fetchRegistrarHistory(idno: string): Promise<RegistrarHistoryEntry[]> {
  const { data } = await apiClient.get<{ entries: RegistrarHistoryEntry[] }>(
    `/admin/registrars/${idno}/history`,
  );
  return data.entries ?? [];
}

// caiet UC21-08 — the credential's own trail, so the file's "Istoric" can show
// the switches an administrator flipped on the "Sistem Registrator" tab. It
// lives in auth-service; the read is scoped to this Registrar by the bff.
export interface CredentialHistoryEntry {
  id: string;
  changeType: string;
  actorId: string;
  actorRole: string;
  recordedAtIso: string;
  addedAddresses: string[];
  removedAddresses: string[];
}

export async function listCredentialHistory(
  clientId: string,
  registrarId: string,
): Promise<CredentialHistoryEntry[]> {
  const { data } = await apiClient.get<{ entries: CredentialHistoryEntry[] }>(
    `/admin/credentials/${encodeURIComponent(clientId)}/history`,
    { params: { registrarId } },
  );
  return data.entries ?? [];
}
